import { type GetClientOrderQuery, OrderShipmentStatus } from '@codezo/graphql';
import { ExternalLink, Truck } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SHIPPING_METHOD_LABEL } from '../../../lib/shipping';
import { formatDate } from '../../../lib/utils';

type Order = NonNullable<GetClientOrderQuery['getClientOrder']>;
type OrderItem = Order['items'][number];
type ItemShipment = NonNullable<OrderItem['shipments']>[number];

/**
 * The boxes carrying one product, under its Shipping step.
 *
 * ── The step this fills ──
 *
 * "Production & delivery" had a Shipping row with a date on it and nothing else.
 * The date comes from `item.shippedAt`, which echoes the FIRST box carrying the
 * product — so a product travelling in two boxes showed one date, no carrier, no
 * tracking, and no sign the second box existed. Under a combination plan it
 * showed nothing at all until something moved: shipping there belongs to the
 * plan's shipments rather than to the item, which is also why `item.delivery` is
 * null on those orders.
 *
 * `OrderItem.shipments` is the answer to that — the boxes carrying THIS product,
 * with how much of it each holds. A product with no plan returns exactly one
 * entry, so nothing here branches on whether a plan exists: one row is the
 * ordinary case, several is the plan case, and the same code draws both.
 *
 * ── Read-only, deliberately ──
 *
 * The studio's copy of this carries the box's own `availableActions`; a client
 * never ships their own order, so this stays an account of where the goods are.
 * That is the same split the deliveries rail makes.
 *
 * ── No money here ──
 *
 * No price is shown and none is summed. Only one shipment per leg bears the
 * agreed freight and the rest read zero, so adding these up says nothing — and a
 * per-product share of a leg's shipping does not exist, because the leg was
 * priced as one movement. What shipping cost belongs to the order's totals.
 */
export function ClientItemShipments({ item }: { item: OrderItem }) {
	const shipments = item.shipments ?? [];

	// Every product is put on a box at acceptance, so this is the pre-acceptance
	// and historical-order case rather than a state the client sits in. The step
	// keeps its own date either way; this just adds nothing to it.
	if (shipments.length === 0) return null;

	return (
		<div className="flex flex-col gap-2.5">
			{shipments.map((entry) => (
				<ShipmentRow key={entry.shipment.id} entry={entry} itemQuantity={item.quantity} />
			))}
		</div>
	);
}

/**
 * One box: what of this product is in it, where it has got to, and how to follow
 * it.
 *
 * Laid out like the studio's equivalent and the sample rounds above it — the
 * naming line first, tracking on a quieter line of its own beneath — so the two
 * halves of "Production & delivery" read as one passage rather than two designs.
 */
function ShipmentRow({ entry, itemQuantity }: { entry: ItemShipment; itemQuantity: number }) {
	const { shipment, quantity } = entry;

	// Only worth saying when the box holds PART of the product — that is the
	// whole reason there is more than one row. "250 of 250" is noise.
	const portion =
		quantity > 0 && quantity < itemQuantity
			? `${quantity.toLocaleString()} of ${itemQuantity.toLocaleString()}`
			: null;

	// The one date that means it is here, then the one that means it left, then
	// the forecast. Never more than one: three dates on a row is a table, and the
	// reader is asking a single question.
	const when = shipment.actualDeliveryDate
		? `delivered ${formatDate(shipment.actualDeliveryDate)}`
		: shipment.shippedAt
			? `shipped ${formatDate(shipment.shippedAt)}`
			: shipment.estimatedDeliveryDate
				? `est. ${formatDate(shipment.estimatedDeliveryDate)}`
				: null;

	const tracking = [shipment.carrier, shipment.trackingNumber].filter(Boolean).join(' ');

	return (
		<div>
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
				<span className="min-w-0 flex-1 text-xs text-text-2">
					{[
						`Shipment ${shipment.sequence}`,
						portion,
						shipment.shippingMethod
							? SHIPPING_METHOD_LABEL[shipment.shippingMethod]
							: null,
						when,
					]
						.filter(Boolean)
						.join(' · ')}
				</span>
				<span className="shrink-0 text-2xs text-text-4">
					{STATUS_LABEL[shipment.status]}
				</span>
			</div>

			{tracking && (
				<div className="mt-1.5 flex flex-wrap items-center gap-2">
					<Truck size={13} className="shrink-0 text-text-3" aria-hidden />
					<span className="font-mono text-xs text-text-4">{tracking}</span>
					{/* Only where the server resolved somewhere real to go. Never built
					    from the carrier name: that column is free text predating the
					    picker, so a row holds whatever was typed and a guessed link
					    that 404s is worse than the plain number. */}
					{shipment.trackingUrl && (
						<Button
							size="xs"
							variant="outline"
							onClick={() => window.open(shipment.trackingUrl!, '_blank', 'noopener')}
						>
							Track
							<ExternalLink size={12} data-icon="inline-end" />
						</Button>
					)}
				</div>
			)}
		</div>
	);
}

/**
 * The client's words for where a box is.
 *
 * A Record over the enum rather than an if-chain, so a new status breaks the
 * build instead of rendering blank. These match the wording the shipments list
 * and the deliveries rail already use — a client meets one vocabulary for a box,
 * not three.
 */
const STATUS_LABEL: Record<OrderShipmentStatus, string> = {
	[OrderShipmentStatus.Pending]: 'Preparing',
	[OrderShipmentStatus.Shipped]: 'In transit',
	[OrderShipmentStatus.Delivered]: 'Delivered',
	[OrderShipmentStatus.Cancelled]: 'Cancelled',
};
